import { z } from 'zod';
import { SemVer } from './common.ts';
import { PackRole, packRoleIssues } from './entitlement.ts';

/** Every port name. Profiles bind adapters by these names; manifests `require` them. */
export const PortName = z
  .enum([
    'ModelGateway',
    'ReasoningRuntime',
    'WorkflowEngine',
    'ToolGateway',
    'ActionLedger',
    'SearchIndex',
    'ObjectStore',
    'EventBus',
    'IdentityProvider',
    'PolicyEngine',
    'SecretsProvider',
    'CredentialBroker',
    'Telemetry',
    'Cache',
    'Clock',
    'IdGenerator',
    'TaskStore',
    'UserDirectory',
    /** Added by 09: durable consumer groups over the event log (`EventTransport` port). */
    'EventTransport',
    /** Added by 10: the knowledge system (embedded engine or the standalone service). */
    'KnowledgeProvider',
    /** Added by 21c: the document register (`documents-embedded` default; token `DOCUMENT_STORE`). */
    'DocumentStore',
    /**
     * Added by 23: optional model catalogue discovery (`model-catalog-static`, `model-catalog-openrouter`). Lists
     * metadata only; never approves, creates profiles or runs inference.
     */
    'ModelCatalogProvider',
  ])
  .meta({ id: 'PortName' });
export type PortName = z.infer<typeof PortName>;
export const PORT_NAMES: readonly PortName[] = PortName.options;

/**
 * 17c0 (review): a `datasetRef` must stay inside the module root: relative, forward slashes, no `.`/`..` segment,
 * no drive letter, no NUL. Returns the problem, or undefined when the path is acceptable.
 */
export function datasetRefIssue(ref: string): string | undefined {
  if (ref.length === 0) return 'datasetRef is empty';
  if (ref.includes('\0')) return 'datasetRef contains NUL';
  if (ref.includes('\\')) return 'datasetRef must use forward slashes';
  if (ref.startsWith('/') || /^[A-Za-z]:/.test(ref)) return 'datasetRef must be relative to the module root';
  if (ref.split('/').some((seg) => seg === '..' || seg === '.' || seg === ''))
    return "datasetRef must not contain '.', '..' or empty segments";
  return undefined;
}

/** Module (core module or pack) name: lowercase kebab case. */
export const ModuleName = z.string().regex(/^[a-z][a-z0-9-]*$/, 'module name must be lowercase kebab case');

/**
 * `module.yaml` (PLATFORM-PLAN §2.2). Relative paths resolve against the module root.
 * Database schema defaults to `core_<name>` for `type: core` and `mod_<name>` otherwise (hyphens → underscores).
 */
export const ModuleManifest = z
  .object({
    apiVersion: z.literal('agentic.platform/v1'),
    kind: z.literal('Module'),
    metadata: z.object({
      name: ModuleName,
      version: SemVer,
      owner: z.string().min(1),
      description: z.string().optional(),
    }),
    spec: z
      .object({
        type: z.enum(['core', 'pack', 'system']).default('pack'),
        /** Explicit Postgres schema override, e.g. `core_ledger`. */
        schema: z
          .string()
          .regex(/^[a-z_][a-z0-9_]{0,62}$/)
          .optional(),
        /** Ports that must be bound (by the profile or by an earlier module) before `register`. */
        requires: z.array(PortName).default([]),
        /** Other modules by name, optionally `name@<semver range>`. Packs keep this empty. */
        dependsOn: z.array(z.string()).default([]),
        provides: z
          .object({
            /** Ports this module binds in `register` (used to order modules). */
            ports: z.array(PortName).default([]),
            agents: z.array(z.string()).default([]),
            tools: z.array(z.string()).default([]),
            mcpServers: z.array(z.string()).default([]),
            policies: z.array(z.string()).default([]),
            workflows: z.array(z.string()).default([]),
            /** Added by 17a: process definitions (`name@version`) the module registers with `registerProcess`. */
            processes: z.array(z.string()).default([]),
            /**
             * Added by 17a (parent F10): pack-owned evaluation suites. `suite: 'dataset'` runs the platform dataset
             * runner over `datasetRef`; any other value names a pack-defined suite kind (registered with the SDK's
             * `registerEvalSuite`). The evaluation catalog lists them as `<module>.<name>` (17c0).
             * 17c0: `datasetRef` is optional (a pack-defined suite may not read a dataset) but required for
             * `suite: 'dataset'`.
             */
            evaluations: z
              .array(
                z
                  .object({
                    name: z.string().regex(/^[a-z][a-z0-9_.-]*$/, 'evaluation name must be lowercase'),
                    suite: z.string().min(1),
                    datasetRef: z.string().min(1).optional(),
                  })
                  .refine((e) => e.suite !== 'dataset' || e.datasetRef !== undefined, {
                    message: "suite 'dataset' needs a datasetRef",
                    path: ['datasetRef'],
                  })
                  .superRefine((e, ctx) => {
                    const issue = e.datasetRef === undefined ? undefined : datasetRefIssue(e.datasetRef);
                    if (issue) ctx.addIssue({ code: 'custom', message: issue, path: ['datasetRef'] });
                  }),
              )
              .default([]),
            /**
             * Added by 21 step 0 (brief 21 §1, 21e): mock-system domains the pack registers with
             * `modules/mock-systems` (`serve` loads them from the same manifest list as the platform). `entry` is a
             * module-relative path exporting the domain. No jobs and no templates here.
             */
            mockDomains: z
              .array(
                z.object({
                  name: z
                    .string()
                    .regex(/^[a-z][a-z0-9-]*$/, 'mock domain name must be lowercase kebab case'),
                  entry: z.string().min(1),
                }),
              )
              .optional(),
            migrations: z.string().optional(),
            seeds: z.string().optional(),
            evals: z.string().optional(),
            ui: z.string().optional(),
            events: z
              .object({
                publishes: z.array(z.string()).default([]),
                subscribes: z.array(z.string()).default([]),
              })
              .prefault({}),
          })
          .prefault({}),
        /**
         * Added by 17a: pack-declared roles. Installing a pack never assigns them: people get them through IdP
         * claims or tenant configuration (`bindsVia`).
         */
        roles: z.array(PackRole).default([]),
      })
      .prefault({}),
  })
  .superRefine((m, ctx) => {
    for (const issue of packRoleIssues(m.metadata.name, m.spec.roles)) {
      ctx.addIssue({ code: 'custom', path: ['spec', 'roles'], message: issue });
    }
  })
  .meta({ id: 'ModuleManifest' });
export type ModuleManifest = z.infer<typeof ModuleManifest>;
export type ModuleManifestInput = z.input<typeof ModuleManifest>;

/** Postgres schema for a module manifest (`spec.schema` or the naming convention). */
export function moduleSchemaName(manifest: Pick<ModuleManifest, 'metadata' | 'spec'>): string {
  if (manifest.spec.schema) return manifest.spec.schema;
  const base = manifest.metadata.name.replaceAll('-', '_');
  return manifest.spec.type === 'core' ? `core_${base}` : `mod_${base}`;
}
